import { Image, ImageProps } from "@heroui/react";

import { useT } from "@/i18n";
import { AvailableFederation } from "@/types/availableFederation.types";
import { Organization, OrganizationReference } from "@/types/organization.types";
import { cn } from "@/utils/cn";
import { getAvatarPlaceholderUrl, resolveLogoUrl } from "@/utils/common";

type OrganizationLogoSource = Organization | OrganizationReference | AvailableFederation;

export type OrganizationLogoProps = Omit<ImageProps, "src" | "alt" | "aria-label"> & {
  organization: OrganizationLogoSource;
  showFallback?: boolean;
};

// `Organization` and `AvailableFederation` both carry the storage-backed `logoFile` (S3)
// + `legacyLogoUrl` pair, so they resolve the same logo as the federation edit page. The
// slim `OrganizationReference` (search results, cells) only has the flat legacy `logo`.
const hasStorageBackedLogo = (
  organization: OrganizationLogoSource
): organization is Organization | AvailableFederation => "legacyLogoUrl" in organization;

export const OrganizationLogo = ({
  organization,
  className,
  classNames,
  ...props
}: OrganizationLogoProps) => {
  const t = useT();

  const label = t("organization.components.logo.label", {
    name: organization.name,
  });

  const src = hasStorageBackedLogo(organization)
    ? resolveLogoUrl(organization) || getAvatarPlaceholderUrl(organization.name)
    : organization.logo || getAvatarPlaceholderUrl(organization.name);

  return (
    <Image
      alt={label}
      aria-label={label}
      radius="sm"
      classNames={{
        ...classNames,
        img: cn("object-contain", classNames?.img),
        wrapper: cn("flex-shrink-0", classNames?.wrapper),
      }}
      className={cn("flex-shrink-0", className)}
      src={src}
      {...props}
    />
  );
};
